/**
 * Agent avatar templates — the brand-owned images an agent's avatar wears.
 *
 * A brand may carry `agentAvatars.textures`: an ORDERED list of sets, each the
 * template images for one palette colour (the M² preset ships its 16 brand-book
 * textures, four per colour). How an agent is drawn:
 *
 *   • its colour has a set            → one image of that set (its variant);
 *   • the brand has sets, but none for
 *     its colour (or it has no colour) → an image of a set picked by its name
 *                                        — exactly what every tenant drew
 *                                        before avatars became a brand asset;
 *   • the brand has no sets           → the brand's mark (`agentAvatars.mark`;
 *                                        the default brand's is the Brij mark)
 *                                        in the mark colour nearest the agent's.
 *
 * The selection below is the pre-existing `getAgentTexture` rule, unchanged —
 * same name hash, same variant arithmetic, same set order (blue, green, red,
 * yellow for M²) — so a brand carrying the M² sets renders pixel-identically to
 * before. The order is why `textures` is an array: JSONB keeps no key order.
 *
 * Which mark colour an agent wears (`agentMarkColor`) — nobody's hue moves:
 *   1. its stored colour IS one of the mark's (the picker stores the base) → it;
 *   2. else the colour it showed until now — a literal's M² hex, its own hex,
 *      or with none the brand palette colour its name hashes to — goes to the
 *      nearest SINGLE-HUE mark colour with a hue, by hue (blue → Sky,
 *      green → Sage, red → Coral, yellow → Gold, purple → Plum, teal → Teal);
 *      a neutral — chroma (max − min channel) under NEUTRAL_CHROMA, so
 *      near-white, grey and near-black alike — to the mark's least chromatic
 *      colour (Slate, itself a neutral, so never worn for a vivid hue). A colour
 *      whose gradient crosses hues (the Brij original) is only ever worn when
 *      picked; a neutral stop (white, black) does not make a gradient cross.
 *
 * Pure module (no React, no DOM) so the node harness can run it.
 */

import type { BrandAgentAvatarSet, BrandAgentMark, BrandAgentMarkColor, BrandConfig } from './types';
import { defaultBrand } from './default';
import { TEXTURE_COLOR_HEX, hashString, hueChroma, hueGap, isTextureColor } from './textures';

/** What `BotAvatar` draws for an agent. */
export type ResolvedAgentAvatar =
  | { kind: 'image'; src: string }
  | { kind: 'mark'; path: string; viewBox: string; gradient: BrandAgentMark['gradient']; stops: BrandAgentMarkColor['stops']; base: string };

type BrandWithAvatars = Pick<BrandConfig, 'agentAvatars' | 'agentColors'>;

/** Upload / storage caps, mirrored by `BrandOverridesRequest.agent_avatars`. */
export const AGENT_AVATAR_LIMITS = {
  maxSets: 12,
  maxImagesPerSet: 8,
  maxImageChars: 200_000,
  maxTotalChars: 1_000_000,
} as const;

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;
const DATA_IMAGE = /^data:image\/(png|jpe?g|gif|webp|svg\+xml)[;,]/i;

/** `#abc` / `abc` / `#AABBCC` → `#AABBCC`; anything else → null. */
export function normalizeHex(value: string | null | undefined): string | null {
  const m = HEX.exec((value ?? '').trim());
  if (!m) return null;
  const digits = m[1].length === 3 ? m[1].split('').map(c => c + c).join('') : m[1];
  return `#${digits.toUpperCase()}`;
}

/**
 * The colour an agent's stored `avatar_color` means, as `#RRGGBB`. The Edit
 * Agent picker stores a TextureColor literal (`blue`…) for the four M² swatches
 * and the raw hex for any other swatch.
 */
export function agentColorHex(raw: string | null | undefined): string | null {
  if (!raw) return null;
  if (isTextureColor(raw)) return TEXTURE_COLOR_HEX[raw];
  return normalizeHex(raw);
}

/**
 * A template image is something a CSS `url()` may load: an app-relative path, an
 * http(s) URL, or an image data URL. Nothing that names another scheme.
 */
export function isSafeAvatarImage(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const v = value.trim();
  if (!v || v !== value) return false;
  if (v.startsWith('/') && !v.startsWith('//')) return true;
  if (/^https?:\/\//i.test(v)) return true;
  return DATA_IMAGE.test(v);
}

/** The brand's usable sets, in order: a parsable colour and at least one safe image. */
export function brandAvatarSets(brand: BrandWithAvatars): BrandAgentAvatarSet[] {
  const raw = brand.agentAvatars?.textures;
  if (!Array.isArray(raw)) return [];
  const out: BrandAgentAvatarSet[] = [];
  for (const set of raw) {
    const color = normalizeHex(set?.color);
    const images = Array.isArray(set?.images) ? set.images.filter(isSafeAvatarImage) : [];
    if (color && images.length > 0) out.push({ color, images });
  }
  return out;
}

export function hasAgentAvatarSets(brand: BrandWithAvatars): boolean {
  return brandAvatarSets(brand).length > 0;
}

/** The set an agent of this stored colour wears, or null when its colour has none. */
export function avatarSetForColor(
  brand: BrandWithAvatars,
  raw: string | null | undefined,
): BrandAgentAvatarSet | null {
  const hex = agentColorHex(raw);
  if (!hex) return null;
  return brandAvatarSets(brand).find(s => s.color === hex) ?? null;
}

/** The brand's palette for an agent with no colour of its own (name-hashed). */
function paletteColor(brand: BrandWithAvatars, hash: number): string {
  const palette = (brand.agentColors ?? []).map(normalizeHex).filter((c): c is string => !!c);
  const list = palette.length > 0 ? palette : Object.values(TEXTURE_COLOR_HEX);
  return list[hash % list.length];
}

/** The mark agents of this brand wear when it has no avatar sets. Every brand
 *  is merged over the default, which carries the Brij mark; a brand's own was
 *  validated on its way in (`_agent_mark` in super_admin/schemas/requests.py,
 *  colours normalized to `#RRGGBB`). */
export function agentMark(brand: BrandWithAvatars): BrandAgentMark {
  const own = brand.agentAvatars?.mark;
  // A mark with no colours cannot be drawn (stored past the validator: a data
  // step, a stale per-tenant cache) — every agent wears the default's instead.
  return own && Array.isArray(own.colors) && own.colors.length > 0 ? own : defaultBrand.agentAvatars!.mark!;
}

/** Below this chroma (max − min channel, 0–1) a colour has no hue worth
 *  matching: grey, near-white, near-black. Slate's own is 0.145. */
const NEUTRAL_CHROMA = 0.15;

/** A mark colour keeps one hue when every non-neutral stop sits within 30° of its base. */
function isSingleHue(color: BrandAgentMarkColor, h: number): boolean {
  return color.stops.map(hueChroma).every(stop => stop.c < NEUTRAL_CHROMA || hueGap(stop.h, h) <= 30);
}

/** The mark colour nearest `hex` (rule in the header). Eight-odd colours:
 *  worked out per call, nothing cached. */
function nearestMarkColor(mark: BrandAgentMark, hex: string): BrandAgentMarkColor {
  const exact = mark.colors.find(c => c.base === hex);
  if (exact) return exact;
  const scored = mark.colors.map(color => ({ color, ...hueChroma(color.base) }));
  const singleHue = scored.filter(({ color, h }) => isSingleHue(color, h));
  const pool = singleHue.length > 0 ? singleHue : scored;
  const target = hueChroma(hex);
  const isNeutral = target.c < NEUTRAL_CHROMA;
  // A neutral goes to a neutral colour, a hue to a colour with one (either list may be empty).
  const side = pool.filter(({ c }) => (c < NEUTRAL_CHROMA) === isNeutral);
  const candidates = side.length > 0 ? side : pool;
  const score = isNeutral
    ? (c: { c: number }) => c.c
    : (c: { h: number }) => hueGap(c.h, target.h);
  let best = candidates[0];
  for (const c of candidates) if (score(c) < score(best)) best = c;
  return best.color;
}

/** The mark colour an agent wears (rule in the header): its stored colour,
 *  else the brand palette colour its name hashes to, mapped. */
export function agentMarkColor(
  brand: BrandWithAvatars,
  name: string,
  raw: string | null | undefined,
): BrandAgentMarkColor {
  const hex = agentColorHex(raw) ?? paletteColor(brand, hashString(name || 'default'));
  return nearestMarkColor(agentMark(brand), hex);
}

/** Gradient stop offsets of a mark (Option C), shared by the avatar and the picker's swatch. */
export const MARK_STOP_OFFSETS = [0.25, 0.59, 1] as const;

/** The Edit Agent picker's swatch for a mark colour: all three stops at the
 *  mark's own offsets, along the mark's own gradient direction (a CSS angle
 *  counts clockwise from "up"; SVG y runs down). */
export function markSwatchBackground(mark: BrandAgentMark, { stops }: BrandAgentMarkColor): string {
  const [x1, y1, x2, y2] = mark.gradient;
  const angle = Math.round((Math.atan2(x2 - x1, y1 - y2) * 180) / Math.PI);
  return `linear-gradient(${angle}deg, ${stops.map((s, i) => `${s} ${Math.round(MARK_STOP_OFFSETS[i] * 100)}%`).join(', ')})`;
}

export function resolveAgentAvatar(
  brand: BrandWithAvatars,
  name: string,
  colorOverride?: string | null,
  variantOverride?: number | null,
): ResolvedAgentAvatar {
  const hash = hashString(name || 'default');
  const hex = agentColorHex(colorOverride);
  const sets = brandAvatarSets(brand);
  if (sets.length > 0) {
    const set = (hex && sets.find(s => s.color === hex)) || sets[hash % sets.length];
    const n = set.images.length;
    const index = variantOverride != null && Number.isFinite(variantOverride)
      ? ((Math.trunc(variantOverride) % n) + n) % n
      : Math.floor(hash / sets.length) % n;
    return { kind: 'image', src: set.images[index] };
  }
  const mark = agentMark(brand);
  const { base, stops } = agentMarkColor(brand, name, colorOverride);
  return { kind: 'mark', path: mark.path, viewBox: mark.viewBox, gradient: mark.gradient, stops, base };
}
